import { useState } from "react";

/**
 * StaffListPanel
 * - staffList: array of staff objects [{ name, designation, qualification, specialization, photo, email, phone, bio }]
 *
 * Usage: <StaffListPanel staffList={StaffList} />
 */

// helper: turn whatever value came from JSON/import into a usable src string
const resolveImageUrl = (
  val,
  {
    fallback = `${process.env.PUBLIC_URL}/images/default-programme-image.jpg`,
    publicPrefix = "/assets/",
  } = {},
) => {
  if (!val) return fallback;

  // If it's already an absolute URL or data URI, return it
  if (typeof val === "string") {
    if (val.startsWith("http") || val.startsWith("data:")) return val;
    // If it looks root-relative (starts with '/'), assume it's in public/ already
    if (val.startsWith("/")) return val;
    // If it is a bare filename like "foo.jpg", try public/images/
    // You can change this to match where you keep default files
    return `${process.env.PUBLIC_URL}/${val}`; // e.g. /default-programme-image.jpg -> /<public-url>/default-programme-image.jpg
  }

  // If imported asset module (webpack/vite) — could be string or { default: string }
  if (typeof val === "object") {
    if (val.default && typeof val.default === "string") return val.default;
    if (val.src && typeof val.src === "string") return val.src; // some loaders
  }

  return fallback;
};
const StaffListPanel = ({ staffList = [] }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [query, setQuery] = useState("");

  // local fallback in case resolveImageUrl is not defined in your file
  const resolveImg = (
    val,
    fallback = `${process.env.PUBLIC_URL || ""}/assets/images/default-passport.png`,
  ) => {
    try {
      // prefer existing helper if available
      // eslint-disable-next-line no-undef
      if (typeof resolveImageUrl === "function")
        return resolveImageUrl(val, { fallback });
    } catch (e) {
      // ignore - use simple mapping
    }
    if (!val) return fallback;
    if (typeof val === "string") {
      if (val.startsWith("http") || val.startsWith("/")) return val;
      return `${process.env.PUBLIC_URL || ""}/${val}`;
    }
    if (val && typeof val === "object") {
      if (val.default) return val.default;
      if (val.src) return val.src;
    }
    return fallback;
  };

  const filtered = staffList.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (s.name || "").toLowerCase().includes(q) ||
      (s.designation || "").toLowerCase().includes(q) ||
      (s.qualification || "").toLowerCase().includes(q) ||
      (s.specialization || "").toLowerCase().includes(q)
    );
  });

  if (!Array.isArray(staffList) || staffList.length === 0) {
    return (
      <div className="progov-staff-empty">
        <h3>Staff</h3>
        <p>No staff list available for this programme/school.</p>
      </div>
    );
  }

  return (
    <section className="progov-staff-root">
      <div className="progov-staff-header">
        <h3>Staff</h3>

        <div className="progov-staff-searchwrap" role="search">
          <input
            type="search"
            className="progov-staff-search"
            placeholder="Search name, role or qualification..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search staff"
          />
          <button
            className="progov-staff-clear"
            onClick={() => setQuery("")}
            title="Clear search"
            aria-hidden={query ? "false" : "true"}
          >
            ×
          </button>
        </div>
      </div>

      <div className="progov-staff-grid" role="list">
        {filtered.map((s, i) => {
          const isOpen = i === expandedIndex;
          const imgSrc = resolveImg(s.photo);
          return (
            <article
              key={s.name + "-" + i}
              role="listitem"
              className={`progov-staff-card ${isOpen ? "open" : ""}`}
              onClick={() => setExpandedIndex(isOpen ? null : i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setExpandedIndex(isOpen ? null : i);
              }}
              tabIndex={0}
              aria-expanded={isOpen}
            >
              <div className="progov-staff-left">
                <img
                  className="progov-staff-photo"
                  src={imgSrc}
                  alt={s.name || "Staff photo"}
                />
              </div>

              <div className="progov-staff-main">
                <div className="progov-staff-top">
                  <div className="progov-staff-name">{s.name}</div>
                  {s.designation && (
                    <div className="progov-staff-role">{s.designation}</div>
                  )}
                </div>

                <div className="progov-staff-tags">
                  {s.qualification && (
                    <div className="progov-staff-tag">{s.qualification}</div>
                  )}
                  {s.specialization && (
                    <div className="progov-staff-tag">{s.specialization}</div>
                  )}
                </div>

                <div className="progov-staff-actions">
                  {s.email && (
                    <a
                      href={`mailto:${s.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="progov-staff-action"
                      title={`Email ${s.name}`}
                    >
                      ✉️
                    </a>
                  )}
                  {s.phone && (
                    <a
                      href={`tel:${s.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className="progov-staff-action"
                      title={`Call ${s.name}`}
                    >
                      📞
                    </a>
                  )}

                  {/* <button
                    className="progov-staff-view"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedIndex(isOpen ? null : i);
                    }}
                    aria-controls={`staff-details-${i}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? "Close" : "View profile"}
                  </button> */}
                </div>

                {isOpen && (
                  <div
                    className="progov-staff-details"
                    id={`staff-details-${i}`}
                  >
                    {s.bio && <div className="progov-staff-bio">{s.bio}</div>}
                    <div className="progov-staff-contact">
                      {s.email && (
                        <a
                          href={`mailto:${s.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="progov-contact-link"
                        >
                          ✉️ {s.email}
                        </a>
                      )}
                      {s.phone && (
                        <a
                          href={`tel:${s.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="progov-contact-link"
                        >
                          📞 {s.phone}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default StaffListPanel;
